import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, BookOpen, Award, Sparkles } from "lucide-react";
import AppHeader from "../components/AppHeader";
import ModuleCard from "../components/ModuleCard";
import ProgressRing from "../components/ProgressRing";
import SearchOverlay from "../components/SearchOverlay";
import BookmarksPanel from "../components/BookmarksPanel";
import { Button } from "../components/ui/button";
import { useUser } from "../context/UserContext";
import { useCourse } from "../context/CourseContext";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { course, loading, totalTopics } = useCourse();
  const [searchOpen, setSearchOpen] = useState(false);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);

  const completed = useMemo(() => new Set(user?.completed_topics || []), [user]);
  const pct = totalTopics > 0 ? (completed.size / totalTopics) * 100 : 0;
  const allDone = totalTopics > 0 && completed.size >= totalTopics;

  useEffect(() => {
    if (allDone) {
      window.parent.postMessage({ type: "COURSE_COMPLETED" }, "*");
    }
  }, [allDone]);

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="p-10 text-muted-foreground">Loading course…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <AppHeader
        onOpenSearch={() => setSearchOpen(true)}
        onOpenBookmarks={() => setBookmarksOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-24">
        {/* Hero / greeting */}
        <section className="pt-12 pb-10 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-end gap-10 justify-between">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Welcome back
              </div>
              <h1
                data-testid="welcome-name"
                className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance"
              >
                Hello, <span className="italic text-brand-amber">{user?.name}</span>.
                <br />
                Let's turn data into decisions.
              </h1>
              <p className="mt-4 max-w-2xl text-muted-foreground text-base sm:text-lg">
                Ten modules, forty hours, zero jargon left unexplained. Pick up where you left
                off — each module ends with a short quiz, and your progress is always saved.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  onClick={() => setSearchOpen(true)}
                  variant="outline"
                  className="rounded-full"
                  data-testid="hero-search-btn"
                >
                  Search the course
                </Button>
                {allDone && (
                  <div className="rounded-full bg-brand-amber/10 text-brand-amber px-4 py-2 text-sm font-medium flex items-center border border-brand-amber/20">
                    <Award className="h-4 w-4 mr-2" />
                    Module Complete! You may now return to the main LMS dashboard to view your official certificate.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-8">
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  Overall progress
                </div>
                <div className="font-display text-5xl mt-1">
                  {completed.size}
                  <span className="text-muted-foreground text-2xl">/{totalTopics}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">topics completed</div>
              </div>
              <ProgressRing value={pct} size={110} stroke={8} />
            </div>
          </div>
        </section>

        {/* Stats strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 mt-10">
          <StatTile icon={<BookOpen className="h-4 w-4" />} label="Modules" value={`${course.modules.length}`} />
          <StatTile
            icon={<Clock className="h-4 w-4" />}
            label="Total duration"
            value={`${course.total_hours} hrs`}
          />
          <StatTile icon={<Sparkles className="h-4 w-4" />} label="Topics" value={`${totalTopics}`} />
          <StatTile
            icon={<Award className="h-4 w-4" />}
            label="Certificate"
            value={allDone ? "Ready" : "Locked"}
          />
        </section>

        {/* Modules grid */}
        <section className="mt-12">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display text-3xl sm:text-4xl">Your 10 modules</h2>
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Click any card to begin
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
            {course.modules.map((m, idx) => {
              const doneCount = m.topics.filter((t) => completed.has(t.id)).length;
              return (
                <ModuleCard
                  key={m.id}
                  module={m}
                  completedCount={doneCount}
                  totalTopics={m.topics.length}
                  delay={idx * 60}
                />
              );
            })}
          </div>
        </section>
      </main>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <BookmarksPanel open={bookmarksOpen} onClose={() => setBookmarksOpen(false)} />
    </div>
  );
}

function StatTile({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-[0.22em]">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl sm:text-3xl mt-2">{value}</div>
    </div>
  );
}
